import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, Validators } from '@angular/forms';
import { MaskPipe } from 'ngx-mask';
import { ApiService } from 'src/app/services/api/api.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { ToastService } from 'src/app/services/toast/toast.service';
import { SignupService } from 'src/app/services/signup/signup.service';
import { StorageService } from 'src/app/services/storage/storage.service';
import { CompanyService } from 'src/app/services/company/company.service';
import { LoadingService } from 'src/app/services/Loading/loading.service';
import Utils, { GetHelper, UtilsValidators, BrValidators } from 'src/app/helpers/utils';
import { CompanyApiUtils } from 'src/app/helpers/utils';
import { Subject } from 'rxjs';
import { takeUntil, debounceTime } from 'rxjs/operators';
import {
  BreadcrumbService,
  BreadcrumbItem,
} from 'src/app/services/breadcrumb/breadcrumb.service';

@Component({
  selector: 'app-dashboard-companies-details',
  templateUrl: './dashboard-companies-details.component.html',
  styleUrls: ['./dashboard-companies-details.component.scss'],
})
export class DashboardCompaniesDetailsComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  activeTab: 'profiles' | 'departments' | 'groups' | 'users' = 'profiles';
  tabs = [
    { label: 'Perfis de acesso', value: 'profiles' },
    { label: 'Departamentos', value: 'departments' },
    { label: 'Grupos de usuários', value: 'groups' },
    { label: 'Usuários', value: 'users' },
  ];

  data: any = null;
  loading = true;
  groups: any[] = [];

  permissionObj = {
    canView: true,
    canAdd: true,
    canFilter: true,
    canInactive: true,
    canReset: true,
  };

  showUserModal = false;
  isMaster = false;
  isSimple = false;
  submiting = false;
  loadingProfiles = false;
  documentExists = true; // Começa TRUE - só libera se API confirmar que NÃO existe
  checkingDocument = false;
  departmentOptions: SelectItem[] = [];
  profileOptions: SelectItem[] = [];

  form = this.formBuilder.group({
    name: [null, [Validators.required, UtilsValidators.fullName]],
    department: [null, [Validators.required]],
    email: [null, [Validators.required, UtilsValidators.email]],
    profile: [null, [Validators.required]],
    document: [null, [Validators.required, Validators.maxLength(14)]],
  });

  constructor(
    private authService: AuthService,
    private api: ApiService,
    private route: ActivatedRoute,
    private router: Router,
    private toast: ToastService,
    private maskPipe: MaskPipe,
    private formBuilder: FormBuilder,
    public signupService: SignupService,
    private storageService: StorageService,
    private companyService: CompanyService,
    private breadcrumbService: BreadcrumbService,
    private loadingService: LoadingService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.setBreadcrumb();
    this.getData();
    this.setPermissions();
    this.tabs = this.tabs.filter((tab) =>
      this.authService.verifyPermission(`de_can_view_company_${tab.value}`)
    );

    // Define a primeira tab disponível como ativa
    if (this.tabs.length > 0) {
      this.activeTab = this.tabs[0].value as any;
    }

    this.form.controls.department.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        this.form.controls.profile.setValue(null);
        if (value) {
          this.getProfiles();
        } else {
          this.profileOptions = [];
        }
      });

    // Observar mudanças no documento para verificar se existe
    this.form.controls.document.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        debounceTime(500)
      )
      .subscribe(async (value) => {
        const documentClean = value ? value.replace(/[^\d]+/g, '') : '';
        
        // SEMPRE reseta para TRUE ao mudar documento - campos NUNCA aparecem antes da API
        this.documentExists = true;
        
        // Quando o documento tem tamanho válido (apenas CPF - 11 dígitos)
        if (documentClean.length === 11) {
          this.checkingDocument = true;
          
          // Validar apenas CPF (11 dígitos)
          if (documentClean.length !== 11) {
            this.checkingDocument = false;
            this.documentExists = true;
            return;
          }
          
          const isValid = BrValidators.cpf(documentClean);
          
          if (!isValid) {
            // Documento inválido - mantém TRUE (não mostra campos)
            this.checkingDocument = false;
            this.documentExists = true;
            return;
          }
          
          // Chamar API para verificar se documento existe
          try {
            const res = await this.api.post({
              route: 'validate-user-paper-exists/',
              body: { document: documentClean },
              token: true,
            });
            
            this.checkingDocument = false;
            
            // Define documentExists baseado na resposta da API
            this.documentExists = res.exists === true;
            
            // SÓ mostra campos se documentExists === FALSE (não existe no sistema)
          } catch (error) {
            console.error('Erro ao validar documento:', error);
            this.checkingDocument = false;
            this.documentExists = true; // Erro = não mostra campos
          }
        } else {
          this.checkingDocument = false;
          this.documentExists = true; // Documento incompleto = não mostra campos
        }
      });

    this.companyService.departmentAdded
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.getDepartments();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onTabChange(tab: 'profiles' | 'departments' | 'groups' | 'users') {
    this.activeTab = tab;
  }

  async getData(page: number = 1) {
    try {
      const id = this.route.snapshot.paramMap.get('id');

      const res = await this.api.get({
        route: `api/v3/companies/${id}/`,
        token: true,
        params: {
          tab: 'company_data',
        },
      });

      this.data = res;
      if (res.status) {
        (this.data as any).statusDisplay = CompanyApiUtils.getStatusDisplay(
          res.status,
          'company'
        );
      }
      await Promise.all([this.getDepartments(), this.getGroups()]);
      this.setBreadcrumb(); // Atualizar breadcrumb após carregar os dados
    } catch (error) {
      console.error(error);
      this.toast.show('error', 'Erro', 'Ocorreu um erro ao carregar os dados.');
    }
    this.loading = false;
  }

  formatCnpj(value: string) {
    const newValue = Utils.formatDocumentForDisplay(value);

    return newValue;
  }

  setPermissions() {
    const permissions = Object.keys(this.permissionObj).map(
      (key) => `de_can_${key.split('can').join('').toLowerCase()}_company_users`
    );

    permissions.forEach((permission, idx) => {
      this.permissionObj[Object.keys(this.permissionObj)[idx]] =
        this.authService.verifyPermission(permission);
    });
  }

  setBreadcrumb(): void {
    const canViewCompanies = this.authService.verifyPermission(
      'de_can_view_companies'
    );
    const companyId = this.route.snapshot.paramMap.get('id');
    const breadcrumbs: BreadcrumbItem[] = [
      {
        label: 'Empresas',
        url: '/app/permissions/companies',
        clickable: canViewCompanies,
      },
      {
        label: this.data?.name || 'Detalhes da empresa',
        url: `/app/permissions/companies/details/${companyId}`,
        clickable: false, // Página atual, não precisa ser clicável
      },
    ];
    this.breadcrumbService.setBreadcrumbs(breadcrumbs);
  }

  async getGroups() {
    try {
      const id = this.route.snapshot.paramMap.get('id');

      const res = await this.api.get({
        route: `api/v3/companies/${id}/`,
        token: true,
        params: {
          tab: 'groups',
        },
      });

      this.groups = res.results || [];
      this.groups.forEach((group: any) => {
        if (group.status) {
          (group as any).statusDisplay = CompanyApiUtils.getStatusDisplay(
            group.status,
            'general'
          );
        }
        if (group.updated_at) {
          (group as any).updated_at_display = CompanyApiUtils.formatDateBR(
            group.updated_at
          );
        }
      });
    } catch (error) {
      console.error(error);
      this.toast.show(
        'error',
        'Erro',
        'Ocorreu um erro ao carregar grupos de usuários.'
      );
    }
  }

  async getDepartments() {
    try {
      const res = await this.api.get({
        route: 'department/',
        token: true,
      });

      this.departmentOptions = res.map((item) => ({
        label: item.name,
        value: item.id,
      }));
    } catch (error) {
      console.error(error);
      this.toast.show('error', 'Erro', 'Ocorreu um erro ao carregar os dados.');
    }
  }

  async getProfiles() {
    this.loadingProfiles = true;
    this.loadingService.show('Carregando perfis de acesso...');
    try {
      const id = this.route.snapshot.paramMap.get('id');

      const res = await this.api.get({
        route: `api/v3/companies/${id}/`,
        params: {
          tab: 'user_profile',
        },
        token: true,
      });

      const profiles = res.results || res;
      this.profileOptions = profiles.map((item) => ({
        label: item.description,
        value: item.id,
        extra: item.id,
      }));
    } catch (error) {
      console.error(error);
      this.toast.show('error', 'Erro', 'Ocorreu um erro ao carregar os dados.');
    } finally {
      this.loadingProfiles = false;
      this.loadingService.hide();
    }
  }

  toggleUserModal() {
    // Se não estiver na aba de usuários, mudar para ela primeiro
    if (this.activeTab !== 'users') {
      this.onTabChange('users');
      this.cdr.detectChanges();
      // Aguardar um pouco para garantir que a mudança de aba seja processada
      setTimeout(() => {
        this.showUserModal = true;
        this.form.reset();
        this.documentExists = true; // Reseta para TRUE
        this.checkingDocument = false;
        this.isSimple = true;
        this.isMaster = false;
        this.form.controls.profile.setValidators([Validators.required]);
        this.form.controls.department.setValidators([Validators.required]);
        this.form.controls.profile.updateValueAndValidity();
        this.form.controls.department.updateValueAndValidity();
        this.cdr.detectChanges();
      }, 150);
    } else {
      this.showUserModal = !this.showUserModal;
      this.form.reset();
      this.documentExists = true; // Reseta para TRUE
      this.checkingDocument = false;

      if (this.showUserModal) {
        this.isSimple = true;
        this.isMaster = false;
        this.form.controls.profile.setValidators([Validators.required]);
        this.form.controls.department.setValidators([Validators.required]);
        this.form.controls.profile.updateValueAndValidity();
        this.form.controls.department.updateValueAndValidity();
      }
    }
  }

  handleCloseModal() {
    this.showUserModal = false;
    this.form.reset();
    this.documentExists = true; // Reseta para TRUE
    this.checkingDocument = false;
    this.isSimple = true;
    this.isMaster = false;
    this.form.controls.profile.setValidators([Validators.required]);
    this.form.controls.department.setValidators([Validators.required]);
    this.form.controls.profile.updateValueAndValidity();
    this.form.controls.department.updateValueAndValidity();
  }

  handleChangeValue(type: string) {
    if (type === 'master') {
      if (this.isMaster) {
        this.isMaster = false;
        this.isSimple = true;
      } else {
        this.isMaster = true;
        this.isSimple = false;
      }
    }

    if (type === 'simple') {
      if (this.isSimple) {
        this.isSimple = false;
        this.isMaster = true;
      } else {
        this.isSimple = true;
        this.isMaster = false;
      }
    }

    this.form.controls.profile.setValidators(
      this.isSimple ? [Validators.required] : []
    );
    this.form.controls.profile.setValue(null);
    this.form.controls.profile.updateValueAndValidity();

    this.form.controls.department.setValidators(
      this.isMaster ? [] : [Validators.required]
    );
    this.form.controls.department.setValue(null);
    this.form.controls.department.updateValueAndValidity();

    this.getProfiles();
  }

  async submit() {
    this.submiting = true;
    try {
      const valuesHelper = new GetHelper(this.form.value);
      const last_name = valuesHelper.get('name').split(' ').slice(1).join(' ');
      const collaboratorPayload: any = {
        email: valuesHelper.get('email'),
        first_name: valuesHelper.get('name').split(' ')[0],
        last_name,
        username: Utils.onlyNumbers(valuesHelper.get('document')),
        is_approver: this.isMaster,
        company: this.data.company_id,
        department: this.isMaster ? null : valuesHelper.get('department'),
        type_dispay:
          valuesHelper.get('document').length === 14
            ? 'Pessoa Jurídica'
            : 'Pessoa Física',
      };

      // Adiciona permission_profile apenas quando NÃO for master
      if (!this.isMaster && valuesHelper.get('profile')) {
        collaboratorPayload.permission_profile = valuesHelper.get('profile');
      }

      const payload = {
        username: Utils.onlyNumbers(valuesHelper.get('document')),
        simple_user: !this.isMaster,
        manager_user: this.isMaster,
        company: [this.route.snapshot.params.id],
        permission_group: this.getPermissionGroup(),
        profiles: this.isMaster ? [] : [valuesHelper.get('profile')],
        department: this.isMaster ? [] : [valuesHelper.get('department')],
      };
      await this.api.post({
        route: 'api/collaborator/',
        token: true,
        body: collaboratorPayload,
      });
      await this.api.post({
        route: 'api/v2/user_group_user/',
        token: true,
        body: payload,
      });
      this.toast.show('info', 'Sucesso', 'Usuário cadastrado com sucesso.');
      this.showUserModal = false;
      this.form.reset();
      this.isSimple = true;
      this.isMaster = false;

      if (this.activeTab === 'users') {
        this.companyService.emitUserAdded();
      }
    } catch (error) {
      console.error(error);
      this.toast.show('error', 'Erro', 'Ocorreu um erro ao salvar os dados.');
    }
    this.submiting = false;
  }

  getPermissionGroup() {
    const departmentLabel = this.departmentOptions.find(
      (item) => item.value === this.form.value.department
    )?.label;
    const group = this.groups.find(
      (item) => item.department === departmentLabel
    );
    return group ? [group.id] : [];
  }

  getDisabled() {
    return this.form.invalid || this.submiting;
  }
}
